import { useState, useEffect, useCallback } from 'react';
import { Shield } from 'lucide-react';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Skeleton, SkeletonTable } from '@/components/ui/Skeleton';
import { formatDateTime } from '@/lib/format';
import { useAuditStore } from '@/stores/auditStore';
import type { AuditLogEntry } from '@nexus/shared/types';

const actionVariant: Record<string, 'success' | 'info' | 'warning' | 'error' | 'purple' | 'default'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
  execute: 'purple',
  classify: 'warning',
  resolve: 'success',
};

function formatChanges(changes?: Record<string, unknown>): string {
  if (!changes) return '--';
  // If there's a human-readable "summary" key, use it
  if (typeof changes.summary === 'string') return changes.summary;
  // Otherwise stringify the changes object compactly
  try {
    const entries = Object.entries(changes);
    if (entries.length === 0) return '--';
    return entries
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
      .join(', ');
  } catch {
    return JSON.stringify(changes);
  }
}

export function AuditLog() {
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const { entries, total, isLoading, error, fetch, clearError } = useAuditStore();

  const fetchWithFilters = useCallback(() => {
    const params: Record<string, string | number | undefined> = {};
    if (actionFilter !== 'all') params.action = actionFilter;
    if (entityFilter !== 'all') params.entityType = entityFilter;
    fetch(params);
  }, [fetch, actionFilter, entityFilter]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchWithFilters();
  }, [fetchWithFilters]);

  const columns: Column<AuditLogEntry>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      sortable: true,
      render: (row) => <span className="text-xs text-surface-400 whitespace-nowrap">{formatDateTime(row.createdAt)}</span>,
    },
    {
      key: 'user',
      header: 'User',
      render: (row) => {
        const displayName = row.userId || 'System';
        const isSystem = !row.userId || displayName === 'System';
        return (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-surface-700 flex items-center justify-center text-[10px] font-bold text-surface-300">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <span className={`text-sm ${isSystem ? 'text-nexus-400 font-medium' : 'text-surface-300'}`}>
              {displayName}
            </span>
          </div>
        );
      },
    },
    {
      key: 'action',
      header: 'Action',
      render: (row) => <Badge variant={actionVariant[row.action] || 'default'}>{row.action}</Badge>,
    },
    {
      key: 'entity',
      header: 'Entity',
      render: (row) => (
        <div>
          <span className="text-xs text-surface-400 capitalize">{row.entityType.replace('_', ' ')}</span>
          <span className="text-xs text-surface-600 mx-1">/</span>
          <span className="text-xs font-mono text-surface-500">{row.entityId}</span>
        </div>
      ),
    },
    {
      key: 'changes',
      header: 'Changes',
      render: (row) => (
        <p className="text-sm text-surface-300 truncate max-w-sm">
          {formatChanges(row.changes)}
        </p>
      ),
    },
  ];

  if (isLoading && entries.length === 0) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        {/* Header skeleton */}
        <div className="flex items-center gap-3">
          <Skeleton className="h-6 w-6 rounded" />
          <div>
            <Skeleton className="h-6 w-28 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        {/* Filters skeleton */}
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-40 rounded-lg" />
          <Skeleton className="h-10 w-44 rounded-lg" />
          <div className="flex-1" />
          <Skeleton className="h-4 w-24" />
        </div>
        {/* Table — 5 columns */}
        <SkeletonTable rows={10} cols={5} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1440px]">
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-nexus-500" />
        <div>
          <h2 className="text-xl font-bold text-surface-100">Audit Log</h2>
          <p className="text-sm text-surface-400 mt-0.5">Complete record of all system actions and changes</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="text-xs text-red-400 hover:text-red-300 cursor-pointer">Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select
          options={[
            { value: 'all', label: 'All Actions' },
            { value: 'create', label: 'Create' },
            { value: 'update', label: 'Update' },
            { value: 'delete', label: 'Delete' },
            { value: 'execute', label: 'Execute' },
            { value: 'classify', label: 'Classify' },
            { value: 'resolve', label: 'Resolve' },
          ]}
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="w-40"
        />
        <Select
          options={[
            { value: 'all', label: 'All Entities' },
            { value: 'email', label: 'Email' },
            { value: 'ticket', label: 'Ticket' },
            { value: 'contact', label: 'Contact' },
            { value: 'invoice', label: 'Invoice' },
            { value: 'partnership', label: 'Partnership' },
            { value: 'growth_signal', label: 'Growth Signal' },
            { value: 'workflow', label: 'Workflow' },
            { value: 'autonomy_rule', label: 'Autonomy Rule' },
            { value: 'call', label: 'Call' },
            { value: 'integration', label: 'Integration' },
          ]}
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          className="w-44"
        />
        <div className="flex-1" />
        <p className="text-xs text-surface-500">{total > 0 ? `${total} total entries` : `${entries.length} entries`}</p>
      </div>

      {/* Table */}
      <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
        <DataTable columns={columns} data={entries} pageSize={15} />
      </div>
    </div>
  );
}
